import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
    Typography, Alert, CircularProgress,
} from '@mui/material';
import { createPart, postMeasurements, genEntryId } from './partsApi';

// ---------------------------------------------------------------------------
// SaveAsPartFeatureDialog — the anonymous->part "Save as part feature" bridge
// (design doc §5:416, §2.5:286): imports ONLY the client-held values already
// in the caller's hands — never adopts a server row keyed by the forgeable
// X-Anonymous-Id header. Durable Parts entities are Clerk-authenticated only,
// so this is meant to be opened from a SIGNED-IN Cockpit result.
//
// Trigger: the PaidAction-gated "Save as part feature" button in CockpitPage.js's
// analysis toolbar (shown only when currentDataset has raw values), which passes
// values/usl/lsl/description from currentDataset — client-held values only, per
// the §5 rule above. This component covers the Parts-side half of the bridge
// (create the part+feature, bulk-ingest the held values).
//
// Batches ingest at <= 500 points/request (doc §3.2 cap), sequentially.
// ---------------------------------------------------------------------------

const INGEST_BATCH_SIZE = 500;

const inputSx = {
    '& .MuiOutlinedInput-root': { color: 'rgba(255, 255, 255, 0.9)' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
};

function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

function SaveAsPartFeatureDialog({ open, onClose, values = [], usl = null, lsl = null, description = '', onSaved }) {
    const [partNumber, setPartNumber] = useState('');
    const [featureName, setFeatureName] = useState(description || '');
    const [unit, setUnit] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(null); // { done, total }
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const reset = () => {
        setPartNumber(''); setFeatureName(description || ''); setUnit('');
        setSubmitting(false); setProgress(null); setError(null); setSuccess(null);
    };

    const handleClose = () => { reset(); onClose?.(); };

    const handleSubmit = async () => {
        if (!partNumber.trim()) { setError('Part number is required.'); return; }
        if (!featureName.trim()) { setError('Feature name is required.'); return; }
        if (!values.length) { setError('No values to save.'); return; }

        setSubmitting(true);
        setError(null);
        try {
            const { ok, status, data } = await createPart({
                partNumber: partNumber.trim(),
                feature: { name: featureName.trim(), usl: usl ?? null, lsl: lsl ?? null, nominal: null, unit: unit.trim() || null },
            });
            if (!ok) {
                setError(status === 409 ? 'A part with that number already exists.' : (data?.error || 'Could not create the part.'));
                setSubmitting(false);
                return;
            }
            const part = data.part || data;
            const feature = data.feature || (Array.isArray(part.features) ? part.features[0] : null);
            const stream = data.stream || (Array.isArray(feature?.streams) ? feature.streams[0] : null);

            const batches = chunk(values, INGEST_BATCH_SIZE);
            let done = 0;
            setProgress({ done, total: values.length });
            for (const batch of batches) {
                const points = batch.map((v) => ({ value: v, externalId: genEntryId() }));
                // eslint-disable-next-line no-await-in-loop
                const res = await postMeasurements(feature.id, { points, process: stream?.process_label });
                if (!res.ok) {
                    setError(res.data?.error || 'Some readings could not be saved.');
                    setSubmitting(false);
                    return;
                }
                done += batch.length;
                setProgress({ done, total: values.length });
            }

            setSuccess({ part, feature, stream });
            onSaved?.({ part, feature, stream });
        } catch (e) {
            setError('Network error. Try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}>
            <DialogTitle sx={{ color: 'rgba(255,255,255,0.9)' }}>Save as part feature</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {success ? (
                    <Alert severity="success">
                        Saved {values.length} reading{values.length === 1 ? '' : 's'} to {success.part?.part_number}.
                    </Alert>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            Imports the {values.length} value{values.length === 1 ? '' : 's'} from this analysis into a
                            new part — the values you see now, nothing pulled from the server.
                        </Typography>
                        <TextField
                            label="Part number" size="small" fullWidth sx={inputSx} autoFocus
                            value={partNumber} onChange={(e) => setPartNumber(e.target.value)}
                            disabled={submitting} inputProps={{ maxLength: 120 }}
                        />
                        <TextField
                            label="Feature name" size="small" fullWidth sx={inputSx}
                            value={featureName} onChange={(e) => setFeatureName(e.target.value)}
                            disabled={submitting} inputProps={{ maxLength: 160 }}
                        />
                        <TextField
                            label="Unit" size="small" fullWidth sx={inputSx}
                            value={unit} onChange={(e) => setUnit(e.target.value)}
                            disabled={submitting} inputProps={{ maxLength: 24 }}
                        />
                        {progress && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                                Saving readings… {progress.done}/{progress.total}
                            </Typography>
                        )}
                        {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {success ? (
                    <Button variant="contained" onClick={handleClose}>Done</Button>
                ) : (
                    <>
                        <Button onClick={handleClose} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
                        <Button
                            variant="contained" onClick={handleSubmit} disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                        >
                            {submitting ? 'Saving…' : 'Save'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default SaveAsPartFeatureDialog;
